import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Empty, Form, Input, Modal,
  Row, Select, Space, Table, Tabs, Tag, Tooltip, Typography, message
} from 'antd';
import {
  ReloadOutlined, FileSearchOutlined, EditOutlined, BankOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { ErpMasterShell } from '../../../../components/common/erp-grid';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { bankReconciliationApi } from './bankReconciliationApi';
import {
  CHEQUE_STATUS_COLOR, CHEQUE_STATUS_LABEL,
  CHEQUE_TRANSITIONS, canTransitionChequeStatus,
  buildChequeEventPayload, formatBRSAmount
} from './bankReconciliationHelpers';
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';
import '../accounts.css';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Cheque Tracking page (Stage 6E.1).
// Lists every cheque-mode payment + lets the operator step through
// status transitions (deposit → cleared / bounced / cancelled /
// stopped). Bounced / cancelled / stopped trigger L1 Approval-MFA.
// ─────────────────────────────────────────────────────────────────

const PROTECTED_STATUSES = new Set(['bounced', 'cancelled', 'stopped']);

function nextOptions(currentStatus) {
  const next = CHEQUE_TRANSITIONS[currentStatus || 'pending'] || [];
  return next.map((s) => ({
    value: s,
    label: CHEQUE_STATUS_LABEL[s] || s
  }));
}

function StatusUpdateModal({ open, payment, onClose, onUpdated }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState(null);
  const { runWithApproval } = useApprovalMfa();

  useEffect(() => { if (open) { form.resetFields(); setNewStatus(null); } }, [open, form]);

  if (!payment) return null;

  const current = payment.chequeStatus || 'pending';
  const opts    = nextOptions(current);
  const showBounceReason = newStatus === 'bounced';

  const save = useCallback(async () => {
    let values;
    try { values = await form.validateFields(); }
    catch (_e) { return; }
    if (!canTransitionChequeStatus(current, values.newStatus)) {
      message.error(`Cheque cannot move from ${current} to ${values.newStatus}.`);
      return;
    }
    const payload = buildChequeEventPayload({
      newStatus:     values.newStatus,
      eventDate:     values.eventDate?.format('YYYY-MM-DD') || null,
      referenceNo:   values.referenceNo || null,
      bounceReason:  values.bounceReason || null,
      remarks:       values.remarks || null
    });
    const needsApproval = PROTECTED_STATUSES.has(values.newStatus);
    setSaving(true);
    try {
      if (needsApproval) {
        const opKey = values.newStatus === 'bounced'  ? 'accounts.cheque.bounced'
                    : values.newStatus === 'stopped'  ? 'accounts.cheque.stopped'
                    : 'accounts.cheque.cancelled';
        await runWithApproval({
          operationKey: opKey,
          resourceType: 'cheque',
          resourceId:   payment.paymentId,
          run: ({ challengeId }) =>
            bankReconciliationApi.updateChequeStatus(
              payment.paymentId, payload, { approvalMfaChallengeId: challengeId })
        });
      } else {
        await bankReconciliationApi.updateChequeStatus(payment.paymentId, payload);
      }
      message.success('Cheque status updated.');
      await onUpdated();
    } catch (e) {
      message.error(friendlyAccountsErrorMessage(e, 'cheque_register'));
    } finally { setSaving(false); }
  }, [form, current, payment, onUpdated, runWithApproval]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={save}
      okText="Update status"
      confirmLoading={saving}
      title={`Update cheque status — ${payment.paymentNo || payment.chequeNo || 'cheque'}`}
      destroyOnClose
      width={560}>
      <Form form={form} layout="vertical" preserve={false}
            initialValues={{ eventDate: dayjs() }}>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">Current status: </Text>
          <Tag color={CHEQUE_STATUS_COLOR[current]}>
            {CHEQUE_STATUS_LABEL[current]}
          </Tag>
        </div>
        {opts.length === 0 ? (
          <Alert type="info" showIcon
            message="This cheque is in a terminal status. No further transitions are allowed."
          />
        ) : (
          <>
            <Form.Item label="New status" name="newStatus"
                       rules={[{ required: true, message: 'Pick a new status.' }]}>
              <Select onChange={setNewStatus} options={opts} />
            </Form.Item>
            <Form.Item label="Event date" name="eventDate"
                       rules={[{ required: true, message: 'Event date required.' }]}>
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
            <Form.Item label="Bank reference / UTR" name="referenceNo">
              <Input maxLength={120} placeholder="Optional" />
            </Form.Item>
            {showBounceReason ? (
              <Form.Item label="Bounce reason" name="bounceReason"
                         rules={[{ required: true,
                                    message: 'A bounce reason is required.' }]}>
                <Input.TextArea rows={2} maxLength={2000}
                  placeholder="E.g. Insufficient funds / Signature mismatch" />
              </Form.Item>
            ) : null}
            <Form.Item label="Remarks" name="remarks">
              <Input.TextArea rows={2} maxLength={2000} />
            </Form.Item>
            {PROTECTED_STATUSES.has(newStatus) ? (
              <Alert type="warning" showIcon
                message="This status change requires Approval-MFA."
                description="It updates the cheque status only. Book the offsetting Journal Voucher separately if accounting reversal is required."
              />
            ) : null}
          </>
        )}
      </Form>
    </Modal>
  );
}

function EventsModal({ open, payment, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !payment) return;
    setLoading(true);
    bankReconciliationApi.listChequeEvents({ paymentId: payment.paymentId })
      .then((out) => setEvents(out?.rows || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [open, payment]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={`Cheque events — ${payment?.paymentNo || payment?.chequeNo || ''}`}
      width={760}>
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={[
          { title: 'Date', dataIndex: 'eventDate', width: 100 },
          { title: 'Old → New', key: 'transition', width: 200,
            render: (_, r) => (
              <Space size={4}>
                <Tag>{r.oldStatus || 'unset'}</Tag>
                <span>→</span>
                <Tag color={CHEQUE_STATUS_COLOR[r.newStatus]}>
                  {CHEQUE_STATUS_LABEL[r.newStatus] || r.newStatus}
                </Tag>
              </Space>
            )},
          { title: 'Reference', dataIndex: 'referenceNo', ellipsis: true },
          { title: 'Reason / Remarks', key: 'note',
            render: (_, r) => r.bounceReason || r.remarks
                              || <Text type="secondary">—</Text> }
        ]}
        dataSource={events}
        pagination={false}
        locale={{ emptyText: <Empty description="No events yet" /> }}
      />
    </Modal>
  );
}

export default function ChequeTrackingPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [chequeStatus, setChequeStatus] = useState('');
  const [q, setQ] = useState('');
  const [active, setActive] = useState(null);
  const [eventsActive, setEventsActive] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const params = {};
      if (chequeStatus) params.chequeStatus = chequeStatus;
      if (q) params.q = q;
      const out = await bankReconciliationApi.listCheques(params);
      setRows(out?.rows || []);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, 'cheque_register'));
    } finally { setLoading(false); }
  }, [chequeStatus, q]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const columns = useMemo(() => ([
    { title: 'Cheque No', dataIndex: 'chequeNo', key: 'cn', width: 130,
      render: (v) => v ? <code style={{ fontSize: 12 }}>{v}</code>
                       : <Text type="secondary">—</Text> },
    { title: 'Payment No', dataIndex: 'paymentNo', key: 'pn', width: 130 },
    { title: 'Type', dataIndex: 'paymentType', key: 'pt', width: 80,
      render: (v) => <Tag>{v}</Tag> },
    { title: 'Date', dataIndex: 'paymentDate', key: 'pd', width: 100 },
    { title: 'Bank Account', dataIndex: 'bankLedgerName', key: 'ba',
      render: (v, r) => v
        ? <span>
            <BankOutlined style={{ marginRight: 4, color: '#0369a1' }} />
            {v}{r.bankLedgerCode ? (
              <Text type="secondary" style={{ marginLeft: 4, fontSize: 11 }}>
                {r.bankLedgerCode}
              </Text>
            ) : null}
          </span>
        : <Text type="secondary">—</Text> },
    { title: 'Amount (₹)', dataIndex: 'amount', key: 'amt', width: 130, align: 'right',
      render: (v) => formatBRSAmount(v) },
    { title: 'Cheque Status', dataIndex: 'chequeStatus', key: 'cs', width: 120,
      render: (v) => (
        <Tag color={CHEQUE_STATUS_COLOR[v || 'pending']}>
          {CHEQUE_STATUS_LABEL[v || 'pending']}
        </Tag>
      )},
    { title: 'Reference', dataIndex: 'bankReference', key: 'ref', width: 130,
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Actions', key: 'actions', width: 130, align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Update status">
            <Button size="small" icon={<EditOutlined />}
              disabled={r.status !== 'posted'}
              onClick={() => setActive(r)} />
          </Tooltip>
          <Tooltip title="View events">
            <Button size="small" icon={<FileSearchOutlined />}
              onClick={() => setEventsActive(r)} />
          </Tooltip>
        </Space>
      )}
  ]), []);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: 4 }}>
          <Title level={4} style={{ margin: 0 }}>Cheque Tracking</Title>
          <Button size="small" icon={<ReloadOutlined />}
            onClick={fetchAll} loading={loading}>Refresh</Button>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          Track issued, deposited, cleared and bounced cheques. Bounced / cancelled / stopped status changes require Approval-MFA.
        </div>

        {loadError ? (
          <Alert type="error" showIcon style={{ marginBottom: 10 }}
            message="Could not load cheque register"
            description={friendlyAccountsErrorMessage(loadError, 'cheque_register')}
            action={<Button size="small" onClick={fetchAll}>Retry</Button>} />
        ) : null}

        <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
          <Space wrap>
            <Space>
              <Text>Status:</Text>
              <Select size="small" value={chequeStatus} onChange={setChequeStatus}
                style={{ width: 150 }}
                options={[
                  { value: '',           label: 'All statuses' },
                  { value: 'pending',    label: 'Pending'      },
                  { value: 'deposited',  label: 'Deposited'    },
                  { value: 'cleared',    label: 'Cleared'      },
                  { value: 'bounced',    label: 'Bounced'      },
                  { value: 'cancelled',  label: 'Cancelled'    }
                ]} />
            </Space>
            <Space>
              <Text>Search:</Text>
              <Input.Search size="small" allowClear value={q}
                onChange={(e) => setQ(e.target.value)}
                onSearch={fetchAll}
                placeholder="Cheque no / payment no"
                style={{ width: 240 }} />
            </Space>
          </Space>
        </Card>

        <Card size="small">
          <Table
            size="small"
            rowKey="paymentId"
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 25, size: 'small' }}
            locale={{ emptyText:
              <Empty description={loading ? 'Loading…'
                : friendlyAccountsEmptyMessage('cheque_register')} /> }}
          />
        </Card>

        <StatusUpdateModal
          open={!!active}
          payment={active}
          onClose={() => setActive(null)}
          onUpdated={async () => { setActive(null); await fetchAll(); }}
        />
        <EventsModal
          open={!!eventsActive}
          payment={eventsActive}
          onClose={() => setEventsActive(null)} />
      </div>
    </ErpMasterShell>
  );
}
